import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { MemoizedSelector, Store } from '@ngrx/store';
// eslint-disable-next-line import/no-unresolved
import { TypedAction } from '@ngrx/store/src/models';
import { concatMap, filter, first, map, Observable, of, switchMap } from 'rxjs';

import { LoadShopCartSuccess } from '../../../shopcart/store/shopcart.actions';
import { categorySelectorConditions, lookbookCategorySelectorConditions, pendingGetCartSelectorConditions } from '../constants/configuration.constant';

import { userConsentPolicies } from '@core/domain/constants/user-consent.const';
import { PageSection } from '@core/domain/types/page-sections.types';
import { UserConsentService } from '@core/ui/services/user-consent.service';
import { LoadCategorySuccess } from '@pages/category';
import { LoadLookbookPageSuccess } from '@pages/lookbook/store/lookbook.actions';
import { LoadProductPageSuccess } from '@pages/product';
import { productPageProductConditions } from '@shared/analytics/GA4/configs/ga4-configuration-events.constant';
import { getGoogleServicesGtmEnabled } from '@shared/google-services/store/google-services.selectors';

@Injectable()
export class ConfigurationService {
    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly userConsentService: UserConsentService,
    ) { }

    private readonly consilatedValuesNeededForPage:
    Record<PageSection, { selector: MemoizedSelector<any, any, any>; valuesNeeded: (any) => boolean }> = {
            ItxProductPage: productPageProductConditions,
            Itx404ErrorPage: undefined,
            Itx410ErrorPage: undefined,
            ItxAngularAuthenticationPage: undefined,
            ItxAngularAuthenticationRegisterPage: undefined,
            ItxAngularAuthenticationConfirmationPage: undefined,
            ItxAngularAuthenticationRecoveryPage: undefined,
            ItxAngularCmsRenderPage: undefined,
            ItxAngularContactPage: undefined,
            ItxAngularGiftCardPage: undefined,
            ItxAngularGiftCardActivationPage: undefined,
            ItxAngularGiftCardBalancePage: undefined,
            ItxAngularLookbookPage: lookbookCategorySelectorConditions,
            ItxAngularOrderStatus: undefined,
            ItxAngularShopcartPage: pendingGetCartSelectorConditions,
            ItxAngularStoreDetailLocatorPage: undefined,
            ItxAngularStoreLocatorPage: undefined,
            ItxAngularTransactionCancelation: undefined,
            ItxAngularWishlistPage: undefined,
            ItxCategoryPage: categorySelectorConditions,
            ItxCheckoutAuthPage: undefined,
            ItxCheckoutPage: undefined,
            ItxCheckoutPaymentPage: undefined,
            ItxCheckoutRepaymentPage: undefined,
            ItxCheckoutShippingDeliveryAddressPage: undefined,
            ItxCheckoutShippingDeliveryPage: undefined,
            ItxCheckoutShippingDeliverySummaryPage: undefined,
            ItxCheckoutShippingDropPointSummaryPage: undefined,
            ItxCheckoutShippingDropPointPage: undefined,
            ItxCheckoutShippingPage: undefined,
            ItxCheckoutShippingStorePage: undefined,
            ItxCheckoutShippingStoreSummaryPage: undefined,
            ItxCheckoutSummaryPage: undefined,
            ItxErrorPage: undefined,
            ItxHelpPage: undefined,
            ItxHomeBgPage: undefined,
            ItxHomeMenPage: undefined,
            ItxHomePage: undefined,
            ItxHomeWomenPage: undefined,
            ItxMassimoDuttiFeelPage: undefined,
            ItxMyAccountFeelPage: undefined,
            ItxMyAccountFeelBirthdayGiftPage: undefined,
            ItxMyAccountFeelStyleAdvisorPage: undefined,
            ItxMyAccountFeelPersonalTailorPage: undefined,
            ItxMyAccountFeelGiftingPage: undefined,
            ItxMyAccountFeelPaperlessPage: undefined,
            ItxMyAccountFeelPromotionsPage: undefined,
            ItxMyAccountFeelCollectionsPage: undefined,
            ItxMyAccountFeelWhatsappPage: undefined,
            ItxMyAccountFeelExperiencesPage: undefined,
            ItxMyAccountNewsletterPage: undefined,
            ItxMyAccountPage: undefined,
            ItxMyAccountReturnsPage: undefined,
            ItxMyAccountSponsorshipPage: undefined,
            ItxNewsletterPage: undefined,
            ItxOrderConfirmationPage: undefined,
            ItxWorldWidePage: undefined,
            ItxMyAccountAccessDataPage: undefined,
            ItxMyAccountDetailsPage: undefined,
            ItxMyAccountAddressListPage: undefined,
            ItxMyAccountCardsPage: undefined,
            ItxMyAccountMenuPage: undefined,
            ItxMyAccountOrdersPage: undefined,
            ItxAngularMyAccountPage: undefined,
            ItxAngularUserPage: undefined,
            ItxMyAccountFeelActivationPage: undefined,
            ItxMyAccountFeelBenefitsPage: undefined,
            ItxMyAccountFeelHistoryPage: undefined,
            ItxMyAccountFeelHowItWorksPage: undefined,
        };

    private readonly awaitInfoForPage: Record<PageSection, TypedAction<any>> = {
        ItxProductPage: LoadProductPageSuccess,
        Itx404ErrorPage: undefined,
        Itx410ErrorPage: undefined,
        ItxAngularAuthenticationPage: undefined,
        ItxAngularAuthenticationRegisterPage: undefined,
        ItxAngularAuthenticationConfirmationPage: undefined,
        ItxAngularAuthenticationRecoveryPage: undefined,
        ItxAngularCmsRenderPage: undefined,
        ItxAngularContactPage: undefined,
        ItxAngularGiftCardPage: undefined,
        ItxAngularGiftCardActivationPage: undefined,
        ItxAngularGiftCardBalancePage: undefined,
        ItxAngularLookbookPage: LoadLookbookPageSuccess,
        ItxAngularOrderStatus: undefined,
        ItxAngularShopcartPage: LoadShopCartSuccess,
        ItxAngularStoreDetailLocatorPage: undefined,
        ItxAngularStoreLocatorPage: undefined,
        ItxAngularTransactionCancelation: undefined,
        ItxAngularWishlistPage: undefined,
        ItxCategoryPage: LoadCategorySuccess,
        ItxCheckoutAuthPage: undefined,
        ItxCheckoutPage: undefined,
        ItxCheckoutPaymentPage: undefined,
        ItxCheckoutRepaymentPage: undefined,
        ItxCheckoutShippingDeliveryAddressPage: undefined,
        ItxCheckoutShippingDeliveryPage: undefined,
        ItxCheckoutShippingDeliverySummaryPage: undefined,
        ItxCheckoutShippingDropPointSummaryPage: undefined,
        ItxCheckoutShippingDropPointPage: undefined,
        ItxCheckoutShippingPage: undefined,
        ItxCheckoutShippingStorePage: undefined,
        ItxCheckoutShippingStoreSummaryPage: undefined,
        ItxCheckoutSummaryPage: undefined,
        ItxErrorPage: undefined,
        ItxHelpPage: undefined,
        ItxHomeBgPage: undefined,
        ItxHomeMenPage: undefined,
        ItxHomePage: undefined,
        ItxHomeWomenPage: undefined,
        ItxMassimoDuttiFeelPage: undefined,
        ItxMyAccountFeelPage: undefined,
        ItxMyAccountFeelBirthdayGiftPage: undefined,
        ItxMyAccountFeelStyleAdvisorPage: undefined,
        ItxMyAccountFeelPersonalTailorPage: undefined,
        ItxMyAccountFeelGiftingPage: undefined,
        ItxMyAccountFeelPaperlessPage: undefined,
        ItxMyAccountFeelPromotionsPage: undefined,
        ItxMyAccountFeelCollectionsPage: undefined,
        ItxMyAccountFeelWhatsappPage: undefined,
        ItxMyAccountFeelExperiencesPage: undefined,
        ItxMyAccountNewsletterPage: undefined,
        ItxMyAccountPage: undefined,
        ItxMyAccountReturnsPage: undefined,
        ItxMyAccountSponsorshipPage: undefined,
        ItxNewsletterPage: undefined,
        ItxOrderConfirmationPage: undefined,
        ItxWorldWidePage: undefined,
        ItxMyAccountAccessDataPage: undefined,
        ItxMyAccountDetailsPage: undefined,
        ItxMyAccountAddressListPage: undefined,
        ItxMyAccountCardsPage: undefined,
        ItxMyAccountMenuPage: undefined,
        ItxMyAccountOrdersPage: undefined,
        ItxAngularMyAccountPage: undefined,
        ItxAngularUserPage: undefined,
        ItxMyAccountFeelActivationPage: undefined,
        ItxMyAccountFeelBenefitsPage: undefined,
        ItxMyAccountFeelHistoryPage: undefined,
        ItxMyAccountFeelHowItWorksPage: undefined,
    };

    /**
     * Función común para GA4 y ZENIT
     *
     * @param page
     * @returns Observable<any>
     */
    public pageDeferConditions(page: PageSection): Observable<any> {
        const condition: any = this.consilatedValuesNeededForPage[page];
        const awaitTo: any = this.awaitInfoForPage[page];
        if (condition) {
            return this.store.select(condition.selector).pipe(
                concatMap((info) => {
                    if (!condition.valuesNeeded(info)) {
                        return of(page);
                    }
                    return this.actions$.pipe(ofType(awaitTo)).pipe(
                        first(),
                    );
                }),
                first(),
            );
        }
        if (awaitTo) {
            return this.actions$.pipe(ofType(awaitTo)).pipe(first());
        }
        return of(page);
    }

    public getIsGTMLoadedWithActualInfo$<T>(action: any): Observable<T> {
        return this.userConsentService.consentEvent$.pipe(
            filter((policy) => !!policy?.data?.[userConsentPolicies.ANALYTICS]?.allowed),
            switchMap(() => this.store.select(getGoogleServicesGtmEnabled)),
            filter((isEnabled) => !!isEnabled),
            first(),
            map(() => action),
        );
    }
}
